import { Authentication } from '@leedpulse-monorepo/classes';
import { CustomSelect, mergeOptionsWithPinnedLabels, selectedTokens } from '@leedpulse-monorepo/components';
import { useAdminsController } from '../../../contexts/data/admins/AdminsContext';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentProps } from 'react';
import { IAdmin } from '@leedpulse-monorepo/interfaces';

type CustomSelectProps = ComponentProps<typeof CustomSelect>;

export type DebouncedAdminSelectProps = Omit<CustomSelectProps, 'options' | 'onSearch' | 'remoteSearch'> & {
    auth: Authentication | null | undefined;
    debounceTimeout?: number;
};

/**
 * CustomSelect backed by the enabled admins list (no Algolia search).
 * Keeps `remoteSearch` so CustomSelect can offer the synthetic "+" option to
 * create a new admin from a typed id.
 */
export default function DebouncedAdminSelect({
    auth,
    debounceTimeout = 300,
    value,
    ...selectProps
}: DebouncedAdminSelectProps) {
    const adminsController = useAdminsController();

    const [query, setQuery] = useState('');
    const [labelByValue] = useState<Record<string, string>>({});

    const selected = useMemo(() => selectedTokens(value), [value]);

    useEffect(() => {
        adminsController.fetchEnabled();
    }, []);

    const options = useMemo(() => {
        const q = query.trim().toLowerCase();
        return adminsController.enabledResults
            .map((admin: IAdmin) => {
                const id = String(admin.id);
                const app_name = typeof admin.application?.app_name === 'string' ? admin.application.app_name : '';
                return { label: `${app_name} (${id})`, value: id };
            })
            .filter((o) => q === '' || o.label.toLowerCase().includes(q));
    }, [adminsController.enabledResults, query]);

    const selectOptions = useMemo(
        () => mergeOptionsWithPinnedLabels(options, selected, labelByValue),
        [options, selected, labelByValue]
    );

    const onSearch = useMemo(() => debounce(setQuery, debounceTimeout), [debounceTimeout]);

    return (
        <CustomSelect
            {...selectProps}
            value={value}
            remoteSearch
            filterOption={false}
            options={selectOptions}
            onSearch={onSearch}
            notFoundContent={'Aucun résultat'}
        />
    );
}
